import { LocalStorageEntities } from '@/constants/LocalStorageEntities';
import type { StrategyItemSubset } from '@/types/StrategyItemSubset';
import type { IStrategy } from '@/types/strategy';
import type { RemovableRef } from '@vueuse/core';
import { useLocalStorage } from '@vueuse/core';
import type { ComputedRef } from 'vue';
import { computed } from 'vue';
import useUser from './useUser';
import { DisplayName } from '@/types/settings/DisplayNamePreference';
import { Locales } from '@/constants/Locales';
import type { IPortfolioTreeStrategy, IPortfolioTreeSubportfolio } from '@/types/IPortfolioTree';
import { isPortfolioTreeStrategy, isPortfolioTreeSubportfolio } from '@/types/IPortfolioTree';
import type { PortfolioItemResponseDTO, StrategyItemResponseDTO } from '@/api-v2/web/discover';
import { getName } from '@/utils/staticInfo';
import { useItemUnderAnalysisGlobalState } from './useItemUnderAnalysis';
import type { StrategyFactsheetResponseDTO } from '@/api-v2/web/strategies';
import { isStrategyFactsheetResponseDTO } from '@/api-v2/web/strategies';

export default function (): {
  userWantsToUseMask: RemovableRef<boolean>;
  shouldUseMask: ComputedRef<boolean>;
  getStrategyMask: (
    datum:
      | IPortfolioTreeSubportfolio
      | IPortfolioTreeStrategy
      | IStrategy
      | PortfolioItemResponseDTO
      | StrategyItemResponseDTO
      | StrategyItemSubset
      | StrategyFactsheetResponseDTO,
  ) => string;
  getMaskedTicker: (code: string) => string | undefined;
  getMaskedName: (code: string, preference: 'longName' | 'shortName' | 'tickerShortName') => string | undefined;
  getPropertyFromPreference: (preference?: DisplayName) => 'shortName' | 'longName' | 'tickerShortName';
} {
  const { user } = useUser();
  const { portfolioTreeUnderAnalysis } = useItemUnderAnalysisGlobalState();

  const userWantsToUseMask = useLocalStorage(LocalStorageEntities.USE_STRATEGY_MASK_UPDATED, true);

  const hasMask = computed((): boolean => {
    return !!portfolioTreeUnderAnalysis.value?.mask && Object.values(portfolioTreeUnderAnalysis.value.mask).length > 0;
  });

  const shouldUseMask = computed(() => userWantsToUseMask.value && hasMask.value);

  const mask = computed(() => portfolioTreeUnderAnalysis.value?.mask);

  /**
   * We just support English locale for now
   */
  const locale = Locales.ENGLISH;

  const getMaskedName = (
    code: string,
    preference: 'longName' | 'shortName' | 'tickerShortName',
  ): string | undefined => {
    if (!mask.value || !mask.value[code]) return;

    return mask.value[code]?.[preference]?.[locale]?.trim();
  };

  const getMaskedTicker = (code: string): string | undefined => {
    if (!mask.value) return;

    return mask.value[code]?.code?.trim();
  };

  /**
   * We don't support code for masking for now, so just map everything besides long_name and tickerShortName to 'shortName'
   */
  const getPropertyFromPreference = (preference?: DisplayName): 'shortName' | 'longName' | 'tickerShortName' => {
    if (preference === DisplayName.LONG_NAME) return 'longName';

    if (preference === DisplayName.TICKER_SHORT_NAME) return 'tickerShortName';

    return 'shortName';
  };

  /**
   * Gets the entity's masked name if it exists, otherwise returns the name.
   *
   * Unlike what the function name suggests, this also supports passing in portfolios, but the name will be returned
   * verbatim as there is no point in masking portfolios.
   *
   * Also handles simplex's global renaming of strategies.
   *
   * the `transformSnakeToPascal` parameter must be set for certain contexts, but it's not clear why
   *
   */
  const getStrategyMask = (
    datum:
      | IPortfolioTreeSubportfolio
      | IPortfolioTreeStrategy
      | IStrategy
      | PortfolioItemResponseDTO
      | StrategyItemResponseDTO
      | StrategyItemSubset
      | StrategyFactsheetResponseDTO,
  ): string => {
    const name = getName(datum);

    if (!user.value || !shouldUseMask.value) {
      return name;
    }

    const preference = getPropertyFromPreference(user.value.displayNamePreference);

    if (isPortfolioTreeSubportfolio(datum) || 'slug' in datum) {
      return name;
    }

    if (isPortfolioTreeStrategy(datum)) {
      if (datum.strategy) {
        return getMaskedName(datum.strategy.code, preference) ?? name;
      }

      // Enable the mask for `IPortfolioTreeStrategy`s which do not have strategies attached.
      // This can happen on the constituent risk page for injected instruments and direct holdings
      if (datum.reference) {
        return getMaskedName(datum.reference, preference) ?? name;
      }

      return name;
    }

    if (isStrategyFactsheetResponseDTO(datum)) {
      return getMaskedName(datum.strategy.code, preference) ?? name;
    }

    return getMaskedName(datum.code, preference) ?? name;
  };

  return {
    userWantsToUseMask,
    shouldUseMask,
    getStrategyMask,
    getMaskedTicker,
    getMaskedName,
    getPropertyFromPreference,
  };
}
